/**
 * Phase 45 Plan 45-05 — TeamAvatars: up to 3 user circles + "+N" chip.
 *
 * Source: UI-SPEC §Surface 1 Column 6 — `ring-2 ring-ms-surface` so circles
 * pop on the table's hover-tinted background.
 *
 * A11y: each Avatar primitive already exposes `aria-label={name}`. The "+N"
 * chip is announced as e.g. "+2 more team members".
 */
import { Avatar } from '../ui/Avatar';
import type { PortfolioTeamMember } from '../../services/api/portfolio';

export interface TeamAvatarsProps {
  team: PortfolioTeamMember[];
  max?: number;
}

export function TeamAvatars({ team, max = 3 }: TeamAvatarsProps) {
  if (!team || team.length === 0) {
    return <span className="text-[11px] text-ms-text-muted">—</span>;
  }
  const shown = team.slice(0, max);
  const overflow = team.length - shown.length;

  return (
    <div className="flex items-center -space-x-1.5">
      {shown.map((member) => {
        const name = `${member.firstName} ${member.lastName}`.trim() || member.email;
        return (
          <span key={member.id} className="ring-2 ring-ms-surface rounded-full">
            <Avatar name={name} colorSeq={member.avatarColorSeq} avatarUrl={member.avatarUrl} size="xs" />
          </span>
        );
      })}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-ms-surface-2 text-ms-text-secondary text-[9px] font-semibold ring-2 ring-ms-surface"
          aria-label={`+${overflow} more team members`}
        >
          +{overflow}
        </span>
      )}
    </div>
  );
}
